<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CardTaskField from './CardTaskField.vue';

const props = defineProps({
  taskTypes: { type: Array, required: true },
  agents: { type: Array, required: true },
  // The card already knows its customer, so the form does not ask for them again.
  hasContact: { type: Boolean, default: false },
  // The card's main assignee, who takes a new task unless someone else is picked.
  defaultAssigneeId: { type: Number, default: null },
  // Resolves to whether the task was created; the dialog closes only then.
  submit: { type: Function, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

// ComboBox needs a truthy value for every option.
const PLAIN = 'plain';
const NOBODY = 'none';
const IDENTITY_TYPES = ['phone', 'email', 'contact_name'];

const dialogRef = ref(null);
const typeId = ref(PLAIN);
const title = ref('');
const assigneeId = ref(NOBODY);
const dueAt = ref('');
const answers = ref({});
const isSaving = ref(false);

watch(typeId, () => {
  answers.value = {};
});

const typeOptions = computed(() => [
  { value: PLAIN, label: t('CARDS.TASKS.PLAIN') },
  ...props.taskTypes.map(type => ({ value: type.id, label: type.name })),
]);
const agentOptions = computed(() => [
  { value: NOBODY, label: t('CARDS.TASKS.UNASSIGNED') },
  ...props.agents.map(agent => ({ value: agent.id, label: agent.name })),
]);

const taskType = computed(() =>
  props.taskTypes.find(type => type.id === typeId.value)
);
const fields = computed(() =>
  (taskType.value?.form_schema || []).filter(
    field => !(props.hasContact && IDENTITY_TYPES.includes(field.type))
  )
);

// A plain to-do is only a title; a typed task can do without one (it takes the type's name).
const canSubmit = computed(() => !!taskType.value || !!title.value.trim());

const reset = () => {
  typeId.value = PLAIN;
  title.value = '';
  assigneeId.value = props.defaultAssigneeId || NOBODY;
  dueAt.value = '';
  answers.value = {};
};

const open = () => {
  reset();
  dialogRef.value.open();
};

const payload = () => ({
  card_task_type_id: taskType.value?.id,
  title: title.value.trim() || undefined,
  assignee_id: assigneeId.value === NOBODY ? undefined : assigneeId.value,
  due_at: dueAt.value ? new Date(dueAt.value).toISOString() : undefined,
  form_data: taskType.value ? answers.value : undefined,
});

const onConfirm = async () => {
  isSaving.value = true;
  const created = await props.submit(payload());
  isSaving.value = false;
  if (created) dialogRef.value.close();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="xl"
    overflow-y-auto
    :title="$t('CARDS.TASKS.NEW')"
    :confirm-button-label="$t('CARDS.TASKS.CREATE')"
    :disable-confirm-button="!canSubmit || isSaving"
    :is-loading="isSaving"
    @confirm="onConfirm"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1" data-testid="task-new-type">
        <span class="text-sm text-n-slate-12">{{
          $t('CARDS.TASKS.TYPE')
        }}</span>
        <ComboBox v-model="typeId" :options="typeOptions" />
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">{{
          $t('CARDS.TASKS.TITLE_FIELD')
        }}</span>
        <Input
          v-model="title"
          :placeholder="taskType?.name || ''"
          data-testid="task-new-title"
        />
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1" data-testid="task-new-assignee">
          <span class="text-sm text-n-slate-12">{{
            $t('CARDS.TASKS.ASSIGNEE')
          }}</span>
          <ComboBox v-model="assigneeId" :options="agentOptions" />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-12">{{
            $t('CARDS.TASKS.DUE_AT')
          }}</span>
          <Input
            v-model="dueAt"
            type="datetime-local"
            data-testid="task-new-due"
          />
        </div>
      </div>
      <CardTaskField
        v-for="field in fields"
        :key="`${typeId}-${field.key}`"
        v-model="answers[field.key]"
        :field="field"
      />
    </div>
  </Dialog>
</template>
